import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Keyboard, Search, Plus, Home, ArrowLeft } from 'lucide-react';

const shortcuts = [
  { key: '⌘ K', description: 'Open search', icon: Search },
  { key: '?', description: 'Show keyboard shortcuts', icon: Keyboard },
  { key: 'G H', description: 'Go to dashboard', icon: Home },
  { key: 'G F', description: 'Go to formulas', icon: null },
  { key: 'G R', description: 'Go to raw materials', icon: null },
  { key: 'G I', description: 'Go to inventory', icon: null },
  { key: 'G P', description: 'Go to production', icon: null },
  { key: 'G C', description: 'Go to customers', icon: null },
  { key: 'G O', description: 'Go to orders', icon: null },
  { key: 'G Q', description: 'Go to quotes', icon: null },
  { key: 'N F', description: 'New formula', icon: Plus },
  { key: 'N R', description: 'New raw material', icon: Plus },
  { key: 'N C', description: 'New customer', icon: Plus },
  { key: 'N O', description: 'New order', icon: Plus },
  { key: 'N Q', description: 'New quote', icon: Plus },
  { key: 'Esc', description: 'Close modal / Go back', icon: ArrowLeft },
];

export default function KeyboardShortcutsHelp() {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger if typing in an input
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      if (e.key === '?' && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        setIsOpen(prev => !prev);
      }

      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed inset-x-4 top-[10%] md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:w-full md:max-w-lg z-50"
          >
            <div className="bg-white/80 dark:bg-sky-900/30 rounded-2xl shadow-2xl overflow-hidden border border-gray-200 dark:border-sky-700/40">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-sky-700/40">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl">
                    <Keyboard className="h-5 w-5 text-white" />
                  </div>
                  <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Keyboard Shortcuts</h2>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-gray-300 hover:bg-slate-100 dark:hover:bg-sky-800/50 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Content */}
              <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
                <div className="space-y-1">
                  {shortcuts.map((shortcut, index) => (
                    <motion.div
                      key={shortcut.key}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.02 }}
                      className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-slate-50 dark:hover:bg-sky-800/50 transition-colors"
                    >
                      <span className="text-slate-700 dark:text-gray-300">{shortcut.description}</span>
                      <kbd className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 dark:bg-sky-900/50 border border-gray-200 dark:border-gray-600 rounded-md text-xs font-mono text-slate-400 dark:text-gray-400">
                        {shortcut.key}
                      </kbd>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-3 bg-slate-50 dark:bg-gray-900 border-t border-gray-200 dark:border-sky-700/40">
                <p className="text-xs text-slate-500 dark:text-gray-400 text-center">
                  Press <kbd className="px-1.5 py-0.5 bg-white dark:bg-sky-900/50 border border-gray-200 dark:border-gray-600 rounded text-xs">?</kbd> to toggle this menu
                </p>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
